/**
 * Projects Export — column resolution (issue #647).
 *
 * Pure functions that build one CSV row per project service assignment, with
 * the required column set (Surrogate ID, Team, Milestone, Three Rs Key,
 * Service Name, Status, Hosting Environment) plus one extra column per
 * user-selected TagCategory. Kept independent of React/Apollo so it's
 * testable without mounting any component.
 *
 * "Three Rs Key" and "Hosting Environment" are sourced from the global
 * `TagCategory`s of the same name (see tag-entity-spec.md §9 and the
 * `scripts/ea-load/32a-create-groups.mjs` seed script) via
 * each service's applied tag ids — the same `TAGGED_WITH`-backed mechanism
 * the definition-driven `ServiceTagsAndCustomFields` renderer uses on the
 * Service detail page.
 */

import { AssignmentStatus } from '../graphql/types';
import type {
  ProjectMilestone,
  ServiceAssignment,
  Tag,
  TagCategory,
  TagValue,
  TagValueType,
} from '../graphql/types';
import type { ServiceResolutionMap } from './serviceResolution';

/** Display name of the global TagCategory backing the "Three Rs Key" column. */
export const THREE_RS_CATEGORY_NAME = 'Three Rs';

/** Display name of the global TagCategory backing the "Hosting Environment" column. */
export const HOSTING_ENVIRONMENT_CATEGORY_NAME = 'Hosting Environment';

/**
 * Placeholder shown when a *non-tag* cell has no resolvable value (e.g. the
 * service backing an assignment was deleted). Untagged TagCategory cells are
 * NOT given this placeholder — they export as an empty string (issue #713).
 */
export const EXPORT_MISSING_VALUE = '—';

/** Index tags by id for O(1) lookup while resolving a service's applied tags. */
export function buildTagsById(tags: Tag[]): Map<string, Tag> {
  return new Map(tags.map((tag) => [tag.entityId, tag]));
}

/** Index tag categories by name for O(1) lookup of the mandatory Three Rs / Hosting Environment categories. */
export function buildTagCategoriesByName(categories: TagCategory[]): Map<string, TagCategory> {
  return new Map(categories.map((category) => [category.name, category]));
}

/** Format a typed tag value for display, mirroring `ServiceTagsAndCustomFields`' `formatValue`. */
function formatTagValue(value: TagValue | null | undefined, valueType: TagValueType): string {
  if (value === null || value === undefined || value === '') return '';
  if (valueType === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Resolve a single export cell's value for one TagCategory, given the tag
 * ids applied to a service. Multiple tags in the same (`allowMultiple`)
 * category are comma-joined into one cell rather than expanded into extra
 * rows/columns — this keeps the export at exactly one row per assignment.
 *
 * A service with no applied tag in this category (or an unresolvable
 * category) exports as an empty string, not a placeholder (issue #713) —
 * "untagged" is a normal, expected state, not a data error.
 */
export function resolveCategoryValue(
  tagIds: string[],
  tagsById: Map<string, Tag>,
  category: TagCategory | undefined
): string {
  if (!category) return '';

  const isLabelOnly = category.valueType === 'label-only';
  const values = tagIds
    .map((id) => tagsById.get(id))
    .filter((tag): tag is Tag => tag !== undefined && tag.categoryId === category.entityId)
    .map((tag) => (isLabelOnly ? tag.name : formatTagValue(tag.value, category.valueType)))
    .filter((value) => value !== '');

  return values.length > 0 ? values.join(', ') : '';
}

/** Human-readable label for an assignment status, matching `AssignedServicesTable`'s display text. */
export function formatAssignmentStatus(status: string): string {
  switch (status) {
    case AssignmentStatus.Planned:
      return 'Planned';
    case AssignmentStatus.InProgress:
      return 'In Progress';
    case AssignmentStatus.Done:
      return 'Done';
    default:
      return status;
  }
}

/**
 * True when a TagCategory should be offered as a user-selectable extra
 * export column: active, applicable to services, and not one of the two
 * categories already included unconditionally.
 */
export function isSelectableExportCategory(category: TagCategory): boolean {
  if (category.status !== 'active') return false;
  if (
    category.name === THREE_RS_CATEGORY_NAME ||
    category.name === HOSTING_ENVIRONMENT_CATEGORY_NAME
  ) {
    return false;
  }
  const types = category.applicableEntityTypes;
  return !types || types.length === 0 || types.includes('Service');
}

export type ProjectExportRow = Record<string, string>;

export interface BuildProjectExportRowsParams {
  assignments: ServiceAssignment[];
  milestones: ProjectMilestone[];
  serviceResolution: ServiceResolutionMap;
  tagsById: Map<string, Tag>;
  categoriesByName: Map<string, TagCategory>;
  /** Extra TagCategories the user picked in the Export dialog, appended after the required columns in selection order. */
  selectedCategories: TagCategory[];
}

/**
 * Build one export row per service assignment, in the required column order:
 * Surrogate ID, Team, Milestone, Three Rs Key, Service Name, Status, Hosting
 * Environment, then one column per user-selected TagCategory.
 */
export function buildProjectExportRows(params: BuildProjectExportRowsParams): ProjectExportRow[] {
  const {
    assignments,
    milestones,
    serviceResolution,
    tagsById,
    categoriesByName,
    selectedCategories,
  } = params;

  const milestoneNameById = new Map(
    milestones.map((milestone) => [milestone.entityId, milestone.name])
  );
  const threeRsCategory = categoriesByName.get(THREE_RS_CATEGORY_NAME);
  const hostingEnvironmentCategory = categoriesByName.get(HOSTING_ENVIRONMENT_CATEGORY_NAME);

  return assignments.map((assignment) => {
    const service = serviceResolution.get(assignment.serviceId);
    const tagIds = service?.tagIds ?? [];

    const row: ProjectExportRow = {
      'Surrogate ID': service?.shortName || EXPORT_MISSING_VALUE,
      Team: service?.teamName ?? 'Unassigned',
      Milestone: milestoneNameById.get(assignment.milestoneId) ?? assignment.milestoneId,
      'Three Rs Key': resolveCategoryValue(tagIds, tagsById, threeRsCategory),
      'Service Name': service?.name ?? assignment.serviceId,
      Status: formatAssignmentStatus(assignment.assignmentStatus),
      'Hosting Environment': resolveCategoryValue(tagIds, tagsById, hostingEnvironmentCategory),
    };

    for (const category of selectedCategories) {
      row[category.name] = resolveCategoryValue(tagIds, tagsById, category);
    }

    return row;
  });
}

/** Build a `<project-slug>-services-export-<date>.csv` filename. */
export function buildExportFilename(projectName: string): string {
  const slug =
    projectName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'project';
  const date = new Date().toISOString().slice(0, 10);
  return `${slug}-services-export-${date}.csv`;
}
